import { ref } from "vue";
import { token } from "@/stores/token";
import { FUNCTIONS_ORIGIN } from "@/utils/functionsOrigin";

// P2 follow-up (2026-09-19) — the billing ACTIONS, in one place.
//
// End, Keep and Update card are offered in two surfaces: the plan card
// (Checkout.vue, inside OrgSettings) and the sidebar banner (Navbar.vue). Before
// this module the Navbar's "Keep subscription" was a router-link to
// OrgSettings?tab=plan, a query nothing reads, so on the settings page it did
// nothing at all and anywhere else it landed at the top of an unrelated
// section. A control that renders and does nothing is the family recorded
// under CLAUDE.md rule 3. Both surfaces now call these functions, so there
// is one implementation of each action and no second copy to drift.
//
// The server owns every write to the organisation's billing fields. Nothing
// here writes Firestore (tickets/retired/ticket-unsubscribe-client-write-hangs.md).

export const PLAN_PATH = "/OrgSettings";

// After a change, reload so every surface re-reads the org document (Navbar and
// OrgSettings read it once, on mount). On the settings page, come back to the
// plan section; anywhere else, stay where the customer was.
export function reloadAfterBillingChange() {
  const path = window.location.pathname;
  window.location.replace(path === PLAN_PATH ? `${PLAN_PATH}?tab=plan` : path);
}

async function postJson(fn, body) {
  const response = await fetch(`${FUNCTIONS_ORIGIN}/${fn}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  let result = {};
  try { result = await response.json(); } catch (e) { result = {}; }
  return { ok: response.ok && result.success !== false, status: response.status, result };
}

export function useSubscriptionActions({ onChanged } = {}) {
  const store = token();
  const isLoading = ref(false);
  const errorMessage = ref("");

  // End (true) and Keep (false) are the same server call with opposite
  // booleans (D2/D3). Every failure ends the spinner and says what happened.
  const setCancelAtPeriodEnd = async (cancelAtPeriodEnd) => {
    errorMessage.value = "";
    isLoading.value = true;
    let reloading = false;
    try {
      const { ok, result } = await postJson("unsubscribe", {
        organisationId: store.organisationId,
        cancelAtPeriodEnd,
      });
      if (ok) {
        if (onChanged) onChanged();
        reloading = true;
        reloadAfterBillingChange();
        return;
      }
      errorMessage.value = result.error
        || (cancelAtPeriodEnd ? "We could not end your subscription. Please try again." : "We could not keep your subscription. Please try again.");
    } catch (error) {
      console.error("Error changing subscription:", error);
      errorMessage.value = "Something went wrong. Please check your connection and try again.";
    } finally {
      // Stop the spinner on every path except the one that is navigating away.
      if (!reloading) isLoading.value = false;
    }
  };

  const endSubscription = () => setCancelAtPeriodEnd(true);
  const keepSubscription = () => setCancelAtPeriodEnd(false);

  // Opens Stripe's portal on its card-update flow only. Offered in every
  // subscribed state, not only past_due: cards expire while a subscription is
  // healthy, and a customer should not need a failed payment to change card.
  const updateCard = async () => {
    errorMessage.value = "";
    isLoading.value = true;
    let leaving = false;
    try {
      const { ok, result } = await postJson("createBillingPortalSession", { organisationId: store.organisationId });
      if (ok && result.url) {
        leaving = true;
        window.location.href = result.url;
        return;
      }
      errorMessage.value = result.error || "We could not open card updates. Please try again.";
    } catch (error) {
      console.error("Error opening card update:", error);
      errorMessage.value = "Something went wrong. Please check your connection and try again.";
    } finally {
      if (!leaving) isLoading.value = false;
    }
  };

  return { isLoading, errorMessage, endSubscription, keepSubscription, updateCard, postJson };
}
